import React, {
    createContext,
    useContext,
    PropsWithChildren,
    useState,
    useLayoutEffect,
    SetStateAction,
    Fragment,
} from "react";
import { Message } from "semantic-ui-react";
import { makeSuccess, TResult } from "../../../sg-core";
import {
    ApplicationConfiguration,
    createApplicationConfiguration,
    TConfigFn,
} from "./ApplicationConfiguration";
import { useAuthentication } from "../auth/store/authSelectors";
import { useLoadAppParameters } from "../store/selectors/contextSelectors";
import { LoaderSegment } from "../components/LoaderSegment";
import TestBackground from "../../../@common/components/TestBackground";

const modifyConfigContext = createContext<{
    setConfig: (updates: SetStateAction<Partial<ApplicationConfiguration>>) => void;
}>({ setConfig: () => new ApplicationConfiguration() });
export const useModifyApplicationConfiguration = () => useContext(modifyConfigContext);

const configContext = createContext<ApplicationConfiguration>(new ApplicationConfiguration());

export const useApplicationConfiguration = () => {
    return useContext(configContext);
};

export const RenderConfigurations: React.FC<{ render: (props: ApplicationConfiguration) => any }> = ({
    render,
}) => {
    const config = useApplicationConfiguration();
    return <Fragment> {render(config)} </Fragment>;
};

const LoadParameters = () => {
    useLoadAppParameters();
    return null;
};

// Provider
export const ConfigurationProvider: React.FC<PropsWithChildren<{ configFn: TConfigFn }>> = ({
    children,
    configFn,
}) => {
    const { token, anonymousSession } = useAuthentication();
    const { Provider } = configContext;
    const { Provider: ModifyConfigProvider } = modifyConfigContext;

    const [config, setConfig] = useState<TResult<ApplicationConfiguration>>();

    const modifyConfig = (updates: SetStateAction<Partial<ApplicationConfiguration>>) => {
        if (!config?.isSuccess) {
            return;
        }
        const data = updates instanceof Function ? updates(config.value) : updates;
        setConfig((prev) => {
            if (prev?.isSuccess) {
                let value = prev.value;
                Object.keys(data).forEach((key) => {
                    const configurationKey = key as keyof ApplicationConfiguration;
                    if (typeof data[configurationKey] !== "undefined") {
                        //@ts-ignore
                        value[configurationKey] = data[configurationKey];
                    }
                });
                return makeSuccess(value);
            }
            return prev;
        });
    };

    useLayoutEffect(() => {
        const loadConfigs = async () => {
            const configResult = await createApplicationConfiguration(configFn);
            setConfig(configResult);
        };
        loadConfigs();
    }, []);

    if (config?.isFailure) {
        return <Message header="Configuration error" content={config.error.message} negative />;
    }

    if (!config?.value) {
        return <LoaderSegment key="INITIAL_LOADER" />;
    }

    const AdditionalContextProvider = config.value.components.ContextProvider;
    const providerKey = JSON.stringify(config.value);

    return (
        <ModifyConfigProvider value={{ setConfig: modifyConfig }}>
            <Provider key={providerKey} value={config.value}>
                <AdditionalContextProvider
                    token={token || ""}
                    anonymousSession={anonymousSession}
                    registerSettings={config.value.registerSettings}
                >
                    <TestBackground active={config.value.testenviromentBackground} />
                    {children}
                </AdditionalContextProvider>
                <LoadParameters />
            </Provider>
        </ModifyConfigProvider>
    );
};
